import { Link } from 'react-router-dom';
import { ErrorState } from '../../components/ErrorState';
import { PageLoadingState } from '../../components/PageLoadingState';
import { priorityLabels, statusLabels } from '../../domain/fieldWorkRequest';
import { formatDate, useSiteFieldWork } from './site-data';

/** Open field work at the site, with closed requests on request. */
export function SiteWorkTab({
  siteId,
  showClosed,
  onShowClosedChange,
}: {
  siteId: string;
  showClosed: boolean;
  onShowClosedChange: (showClosed: boolean) => void;
}) {
  const { query, requests, open } = useSiteFieldWork(siteId);
  const shown = showClosed ? requests : open;

  if (query.isLoading) return <PageLoadingState label="Loading field work…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Field work could not be loaded"
        rpcName="ListFieldWorkRequests"
        onRetry={() => void query.refetch()}
      />
    );
  }

  return (
    <section aria-label="Field work at this site">
      <label className="site-closed-toggle">
        <span>Include closed</span>
        <input
          type="checkbox"
          checked={showClosed}
          onChange={(event) => onShowClosedChange(event.target.checked)}
          aria-label="Include completed and rejected requests"
        />
      </label>
      {shown.length === 0 ? (
        <p className="empty-note">
          {showClosed
            ? 'No field work recorded for this site.'
            : 'No open field work at this site.'}
        </p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">Request</th>
                <th scope="col">Priority</th>
                <th scope="col">Assignee</th>
                <th scope="col">Status</th>
                <th scope="col">Due</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((request) => (
                <tr key={request.id}>
                  <th scope="row">
                    <Link to={`/field-work/${encodeURIComponent(request.id)}`}>
                      {request.title || 'Untitled request'}
                    </Link>
                  </th>
                  <td>{request.priority ? priorityLabels[request.priority] : '—'}</td>
                  <td>{request.assignee || 'Unassigned'}</td>
                  <td>{statusLabels[request.status]}</td>
                  <td className="mono">{formatDate(request.dueDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
